import { useEffect, useMemo, useState } from "react"

import {
  useAdgroupDuplicatePreview,
  useDuplicateAdgroup,
} from "@/api/hooks/adsManager"
import { useRegions } from "@/api/hooks/campaignForm"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { SearchableMultiSelect } from "@/pages/form/widgets/SearchableMultiSelect"

interface DuplicateAdgroupModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  adgroupId: string | null
  adgroupName?: string
}

type CopyDraft = {
  name: string
  budget: string
  bid: string
  locationIds: string[]
}

function suggestedBaseName(adgroupName: string, suggested?: string | null) {
  return (suggested || `${adgroupName} (Copy)`).trim()
}

function numberedCopyName(base: string, index: number): string {
  if (index === 0) return base
  if (/\(Copy\)\s*$/i.test(base)) {
    return base.replace(/\(Copy\)\s*$/i, `(Copy ${index + 1})`)
  }
  if (/\(Copy\s+\d+\)\s*$/i.test(base)) {
    return base.replace(/\(Copy\s+\d+\)\s*$/i, `(Copy ${index + 1})`)
  }
  return `${base} (${index + 1})`
}

function draftFromPreview(
  data: {
    adgroup_name: string
    suggested_name?: string | null
    is_cbo: boolean
    budget?: number | null
    conversion_bid_price?: number | null
    location_ids?: string[] | null
  },
  index: number,
): CopyDraft {
  const base = suggestedBaseName(data.adgroup_name, data.suggested_name)
  return {
    name: numberedCopyName(base, index),
    budget: data.is_cbo || data.budget == null ? "" : String(data.budget),
    bid:
      data.conversion_bid_price == null
        ? ""
        : String(data.conversion_bid_price),
    locationIds: [...(data.location_ids ?? [])],
  }
}

export function DuplicateAdgroupModal({
  open,
  onOpenChange,
  adgroupId,
  adgroupName,
}: DuplicateAdgroupModalProps) {
  const preview = useAdgroupDuplicatePreview(adgroupId, {
    enabled: open && Boolean(adgroupId),
  })
  const duplicate = useDuplicateAdgroup()

  const [drafts, setDrafts] = useState<CopyDraft[]>([])
  const [formError, setFormError] = useState<string | null>(null)
  const [progressLabel, setProgressLabel] = useState<string | null>(null)
  const copies = drafts.length

  const previewData = preview.data?.data
  const advertiserId = previewData?.advertiser_id ?? null
  const objectiveType = previewData?.objective_type ?? "WEB_CONVERSIONS"

  const regionsQuery = useRegions(advertiserId, objectiveType)
  const regionOptions = useMemo(
    () =>
      (regionsQuery.data ?? []).map((r) => ({
        id: r.id,
        name: r.name,
      })),
    [regionsQuery.data],
  )

  useEffect(() => {
    if (!open) return
    setFormError(null)
    setProgressLabel(null)
    setDrafts([])
    duplicate.reset()
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const data = preview.data?.data
    if (!data) return
    setDrafts([draftFromPreview(data, 0)])
  }, [preview.data])

  const data = preview.data?.data
  const previewError =
    preview.isError
      ? (preview.error as Error)?.message || "Failed to load adgroup settings"
      : null

  function updateDraft(index: number, patch: Partial<CopyDraft>) {
    setDrafts((prev) =>
      prev.map((d, i) => (i === index ? { ...d, ...patch } : d)),
    )
  }

  function addCopy() {
    if (drafts.length >= 3) return
    setDrafts((prev) => {
      const template = prev[0]
      const i = prev.length
      return [
        ...prev,
        {
          name: numberedCopyName(
            template?.name ||
              (data
                ? suggestedBaseName(data.adgroup_name, data.suggested_name)
                : "Copy"),
            i,
          ),
          budget: template?.budget ?? "",
          bid: template?.bid ?? "",
          locationIds: [...(template?.locationIds ?? [])],
        },
      ]
    })
  }

  function removeCopy(index: number) {
    if (index === 0 || drafts.length <= 1) return
    setDrafts((prev) => prev.filter((_, i) => i !== index))
  }

  function toggleLocation(index: number, id: string) {
    setDrafts((prev) =>
      prev.map((d, i) => {
        if (i !== index) return d
        const has = d.locationIds.includes(id)
        return {
          ...d,
          locationIds: has
            ? d.locationIds.filter((x) => x !== id)
            : [...d.locationIds, id],
        }
      }),
    )
  }

  async function handleSubmit() {
    if (!adgroupId || !data) return
    setFormError(null)

    const active = drafts
    for (let i = 0; i < active.length; i++) {
      const d = active[i]
      if (!d.name.trim()) {
        setFormError(`Copy ${i + 1}: name is required`)
        return
      }
      if (d.locationIds.length === 0) {
        setFormError(`Copy ${i + 1}: select at least one country`)
        return
      }
      const budgetNum = d.budget.trim() === "" ? null : Number(d.budget)
      const bidNum = d.bid.trim() === "" ? null : Number(d.bid)
      if (budgetNum != null && Number.isNaN(budgetNum)) {
        setFormError(`Copy ${i + 1}: budget must be a number`)
        return
      }
      if (bidNum != null && Number.isNaN(bidNum)) {
        setFormError(`Copy ${i + 1}: bid must be a number`)
        return
      }
    }

    // Normalize ABO budgets to min $20 before create.
    const normalized = active.map((d) => {
      let budgetNum = d.budget.trim() === "" ? null : Number(d.budget)
      if (!data.is_cbo) {
        if (budgetNum == null || Number.isNaN(budgetNum) || budgetNum < 20) {
          budgetNum = 20
        }
      }
      const bidNum = d.bid.trim() === "" ? null : Number(d.bid)
      return {
        name: d.name.trim(),
        budget: budgetNum,
        bid: bidNum != null && !Number.isNaN(bidNum) ? bidNum : null,
        locationIds: d.locationIds,
      }
    })
    if (!data.is_cbo) {
      setDrafts((prev) =>
        prev.map((d, i) =>
          i < normalized.length
            ? { ...d, budget: String(normalized[i].budget ?? 20) }
            : d,
        ),
      )
    }

    const total = normalized.length
    let made = 0
    try {
      for (let i = 0; i < total; i++) {
        const row = normalized[i]
        setProgressLabel(
          total > 1 ? `Duplicating ${i + 1} of ${total}…` : "Duplicating…",
        )
        await duplicate.mutateAsync({
          adgroupId,
          body: {
            adgroup_name: row.name,
            budget: data.is_cbo ? null : row.budget,
            conversion_bid_price: row.bid,
            location_ids: row.locationIds,
            start_paused: false,
            schedule_start_now: true,
          },
        })
        made += 1
      }
      onOpenChange(false)
    } catch (err) {
      const base = (err as Error)?.message || "Duplicate failed"
      setFormError(
        total > 1 && made > 0 ? `${base} (${made} of ${total} created)` : base,
      )
    } finally {
      setProgressLabel(null)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] max-w-lg flex-col overflow-hidden border-[#4a4a4a] bg-[#2d2d2d] text-white top-[8%] translate-y-0 data-[state=closed]:slide-out-to-top-[8%] data-[state=open]:slide-in-from-top-[8%]">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-white">Duplicate adset</DialogTitle>
          <DialogDescription className="text-[#b0b0b0]">
            {data?.campaign_name
              ? `${data.campaign_name} · ${adgroupId}`
              : adgroupName
                ? `${adgroupName} · ${adgroupId ?? ""}`
                : adgroupId ?? "Loading…"}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-0.5 py-0.5">
          {preview.isLoading && (
            <div className="py-6 text-center text-sm text-white/70">
              Loading settings from TikTok…
            </div>
          )}

          {previewError && (
            <div className="rounded border border-red-700/50 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {previewError}
            </div>
          )}

          {data && !preview.isLoading && drafts.length > 0 && (
            <div className="space-y-4 text-sm">
              {drafts.map((draft, index) => (
                <div key={index} className="space-y-3">
                  <div className="space-y-3 rounded-lg border border-[#4a4a4a] bg-[#262626] p-3">
                    {copies > 1 ? (
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-semibold uppercase tracking-wide text-[#9eabff]">
                          Copy {index + 1}
                        </div>
                        {index > 0 ? (
                          <button
                            type="button"
                            onClick={() => removeCopy(index)}
                            className="text-[11px] font-medium text-[#b0b0b0] transition-colors hover:text-white"
                          >
                            Remove
                          </button>
                        ) : null}
                      </div>
                    ) : null}

                    <div className="space-y-1.5">
                      <Label
                        htmlFor={`dup-name-${index}`}
                        className="text-white/80"
                      >
                        Name
                      </Label>
                      <Input
                        id={`dup-name-${index}`}
                        value={draft.name}
                        onChange={(e) =>
                          updateDraft(index, { name: e.target.value })
                        }
                        className="border-[#555] bg-[#1f1f1f] text-white focus-visible:ring-1 focus-visible:ring-[#8b9aff] focus-visible:ring-offset-0"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5">
                        <Label
                          htmlFor={`dup-budget-${index}`}
                          className="text-white/80"
                        >
                          Daily budget{data.is_cbo ? "" : " (min. $20)"}
                        </Label>
                        <Input
                          id={`dup-budget-${index}`}
                          type="number"
                          min={20}
                          step="0.01"
                          value={draft.budget}
                          onChange={(e) =>
                            updateDraft(index, { budget: e.target.value })
                          }
                          onBlur={() => {
                            if (data.is_cbo) return
                            const raw = draft.budget.trim()
                            if (!raw) {
                              updateDraft(index, { budget: "20" })
                              return
                            }
                            const parsed = Number(raw)
                            if (!Number.isFinite(parsed) || parsed < 20) {
                              updateDraft(index, { budget: "20" })
                            }
                          }}
                          disabled={data.is_cbo}
                          placeholder={
                            data.is_cbo ? "CBO — locked" : "e.g. 20"
                          }
                          className="border-[#555] bg-[#1f1f1f] text-white focus-visible:ring-1 focus-visible:ring-[#8b9aff] focus-visible:ring-offset-0 disabled:opacity-50"
                        />
                        {data.is_cbo && index === 0 ? (
                          <p className="text-[11px] text-white/50">
                            Campaign budget optimization — budget set at
                            campaign level
                          </p>
                        ) : null}
                      </div>
                      <div className="space-y-1.5">
                        <Label
                          htmlFor={`dup-bid-${index}`}
                          className="text-white/80"
                        >
                          Conversion bid
                        </Label>
                        <Input
                          id={`dup-bid-${index}`}
                          value={draft.bid}
                          onChange={(e) =>
                            updateDraft(index, { bid: e.target.value })
                          }
                          placeholder="optional"
                          className="border-[#555] bg-[#1f1f1f] text-white focus-visible:ring-1 focus-visible:ring-[#8b9aff] focus-visible:ring-offset-0"
                        />
                      </div>
                    </div>

                    <div className="space-y-1.5">
                      <Label className="text-white/80">Countries</Label>
                      <SearchableMultiSelect
                        options={regionOptions}
                        selected={draft.locationIds}
                        onToggle={(id) => toggleLocation(index, id)}
                        onClear={() =>
                          updateDraft(index, { locationIds: [] })
                        }
                        inlineList
                        searchPlaceholder="Search countries…"
                        emptyPlaceholder={
                          regionsQuery.isLoading
                            ? "Loading countries…"
                            : "Search countries…"
                        }
                        emptyListText={
                          regionsQuery.isError
                            ? "Failed to load countries"
                            : "No countries available"
                        }
                      />
                    </div>
                  </div>

                  {index === drafts.length - 1 && drafts.length < 3 ? (
                    <button
                      type="button"
                      onClick={addCopy}
                      className="w-full rounded-md border border-dashed border-[#555] bg-transparent px-3 py-2 text-xs font-semibold text-[#b0b0b0] transition-colors hover:border-[#8fa0ef]/50 hover:text-white"
                    >
                      + Add copy
                    </button>
                  ) : null}
                </div>
              ))}

              {(formError || duplicate.isError) && (
                <div className="rounded border border-red-700/50 bg-red-950/40 px-3 py-2 text-sm text-red-300">
                  {formError ||
                    (duplicate.error as Error)?.message ||
                    "Duplicate failed"}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="shrink-0 gap-2 sm:gap-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="rounded-md border border-[#676d78] bg-[#4f5561] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#5a616f]"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={
              !data ||
              preview.isLoading ||
              duplicate.isPending ||
              drafts.length === 0
            }
            onClick={() => void handleSubmit()}
            className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#687bda] disabled:opacity-50"
          >
            {progressLabel
              ? progressLabel
              : duplicate.isPending
                ? "Duplicating…"
                : copies > 1
                  ? `Duplicate ×${copies}`
                  : "Duplicate"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
